import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { 
  FileText, Mic, MicOff, Send, Download, Phone, 
  MapPin, Users, Clock, Loader2, Check, X, ChevronDown, ChevronUp, Euro,
  Receipt, CreditCard, RefreshCw
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkOrderForm = ({ clients = [], onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    client_id: '', client_name: '', client_phone: '', address: '',
    date: new Date().toISOString().split('T')[0],
    description: '', materials: '', workers_count: 1, hours_worked: 0, amount: '',
    issue_receipt: false, payment_mode: 'full', installments_count: 1,
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [signature, setSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [lastOrderData, setLastOrderData] = useState(null);
  const [receiptStatus, setReceiptStatus] = useState(null); // null | 'checking' | 'creating' | 'pending' | 'paid' | 'receipt_ready' | 'error'
  const [listening, setListening] = useState(null); // 'description' | 'materials' | null
  const canvasRef = useRef(null);
  const recognitionRef = useRef(null);

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.phone || '').includes(clientSearch)
  );

  const selectClient = (client) => {
    setSelectedClient(client);
    setForm(f => ({
      ...f,
      client_id: client.client_id,
      client_name: client.name,
      client_phone: client.phone || '',
      address: client.address || '',
    }));
    setClientSearch('');
    setShowClientDropdown(false);
  };

  const clearClient = () => {
    setSelectedClient(null);
    setForm(f => ({ ...f, client_id: '', client_name: '', client_phone: '', address: '' }));
  };

  // Signature canvas
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#09090B';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    if (open) setTimeout(initCanvas, 100);
  }, [open, initCanvas]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setSignature(canvasRef.current.toDataURL('image/png'));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };

  // Voice recognition
  const toggleVoice = (field) => {
    if (listening === field) {
      recognitionRef.current?.stop();
      setListening(null);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Riconoscimento vocale non supportato dal browser');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'it-IT';
    recognition.continuous = true;
    recognition.interimResults = true;
    let finalTranscript = '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + ' ';
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setForm(f => ({ ...f, [field]: (f[field] ? f[field] + ' ' : '') + finalTranscript + interim }));
      finalTranscript = '';
    };
    recognition.onerror = () => setListening(null);
    recognition.onend = () => setListening(null);
    recognition.start();
    recognitionRef.current = recognition;
    setListening(field);
  };

  const handleSubmit = async () => {
    if (!form.client_name.trim()) {
      alert('Inserisci il nome del cliente');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0, signature_base64: signature || '' };
      const res = await axios.post(`${API_URL}/api/worker/work-orders`, payload, { withCredentials: true });
      setLastCreated(res.data.order_id);
      setLastOrderData({ ...form, order_id: res.data.order_id });
      setReceiptStatus(null);
      onCreated?.();
      // Reset form but keep client
      setForm(f => ({
        ...f, description: '', materials: '', workers_count: 1, hours_worked: 0, amount: '',
        issue_receipt: false, payment_mode: 'full', installments_count: 1,
        date: new Date().toISOString().split('T')[0],
      }));
      clearSignature();
      setSignature(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore creazione buono');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (orderId) => {
    try {
      const res = await axios.get(`${API_URL}/api/work-orders/${orderId}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Buono_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Errore download PDF');
    }
  };

  const openWhatsApp = (phone, orderId) => {
    const cleanPhone = (phone || '').replace(/[^0-9+]/g, '');
    const fullPhone = cleanPhone.startsWith('+') ? cleanPhone : `+39${cleanPhone}`;
    const od = lastOrderData || form;
    const amountStr = od.amount ? `\nImporto: € ${parseFloat(od.amount).toFixed(2)} + IVA` : '';
    const rateStr = od.payment_mode === 'installments' && od.installments_count > 1
      ? `\nPagamento dilazionato in ${od.installments_count} rate`
      : '';
    const msg = encodeURIComponent(
      `Buono di Lavoro N. ${orderId}\nData: ${od.date}\nCliente: ${od.client_name}${amountStr}${rateStr}\n\nEsa Impianti di Soldani Giulio & C.\nTel: 335 786 3535\nwww.esaimpiantisnc.com\n\nGrazie per aver scelto i nostri servizi.`
    );
    window.open(`https://wa.me/${fullPhone.replace('+', '')}?text=${msg}`, '_blank');
  };

  // SumUp: create checkout
  const createSumupCheckout = async (orderId) => {
    setReceiptStatus('checking');
    try {
      const res = await axios.post(`${API_URL}/api/work-orders/${orderId}/sumup-checkout`, {}, { withCredentials: true });
      if (res.data.payment_url) {
        window.open(res.data.payment_url, '_blank');
      }
      setReceiptStatus('pending');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore creazione checkout SumUp';
      alert(msg);
      setReceiptStatus('error');
    }
  };

  // SumUp: check payment status
  const checkPaymentStatus = async (orderId) => {
    setReceiptStatus('checking');
    try {
      const res = await axios.get(`${API_URL}/api/work-orders/${orderId}/payment-status`, { withCredentials: true });
      if (res.data.status === 'PAID') {
        setReceiptStatus('paid');
      } else {
        setReceiptStatus('pending');
        alert(`Stato pagamento: ${res.data.status || 'In attesa'}`);
      }
    } catch (e) {
      alert('Errore verifica pagamento');
      setReceiptStatus('error');
    }
  };

  // SumUp: get receipt
  const getReceipt = async (orderId) => {
    setReceiptStatus('creating');
    try {
      await axios.post(`${API_URL}/api/work-orders/${orderId}/receipt`, {}, { withCredentials: true });
      setReceiptStatus('receipt_ready');
      alert('Ricevuta fiscale emessa con successo!');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore emissione ricevuta';
      alert(msg);
      setReceiptStatus('paid');
    }
  };

  const voiceSupported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  return (
    <Card className="border-[#E4E4E7] shadow-sm" data-testid="work-order-form">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <CardTitle className="text-lg font-bold text-[#09090B] flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#0055FF]" />
            Buono di Lavoro
          </span>
          {open ? <ChevronUp className="w-5 h-5 text-[#71717A]" /> : <ChevronDown className="w-5 h-5 text-[#71717A]" />}
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4" data-testid="work-order-form-content">
          {/* Success message */}
          {lastCreated && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="work-order-success">
              <div className="flex items-center gap-2 mb-3">
                <Check className="w-5 h-5 text-[#34C759]" />
                <span className="font-semibold text-[#09090B]">Buono creato!</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => downloadPdf(lastCreated)} data-testid="download-work-order-pdf">
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                {(lastOrderData?.client_phone || form.client_phone) && (
                  <Button size="sm" className="bg-[#25D366] hover:bg-[#1DA851] text-white"
                    onClick={() => openWhatsApp(lastOrderData?.client_phone || form.client_phone, lastCreated)} data-testid="whatsapp-work-order">
                    <FaWhatsapp className="w-4 h-4 mr-1" /> WhatsApp
                  </Button>
                )}
              </div>

              {/* SumUp Payment & Receipt */}
              {lastOrderData?.amount && parseFloat(lastOrderData.amount) > 0 && (
                <div className="mt-3 pt-3 border-t border-green-200 space-y-2">
                  <p className="text-sm font-semibold text-[#09090B]">Pagamento e Ricevuta</p>

                  {/* Create SumUp checkout */}
                  {(!receiptStatus || receiptStatus === 'error') && (
                    <Button size="sm" className="bg-[#1A1A2E] hover:bg-[#2D2D44] text-white w-full"
                      onClick={() => createSumupCheckout(lastCreated)} data-testid="create-sumup-checkout-btn">
                      <CreditCard className="w-4 h-4 mr-1" /> Invia Link Pagamento SumUp
                    </Button>
                  )}

                  {/* Payment pending - check status */}
                  {receiptStatus === 'pending' && (
                    <div className="space-y-2">
                      <p className="text-xs text-[#F59E0B] font-medium">In attesa di pagamento...</p>
                      <Button size="sm" variant="outline" className="w-full"
                        onClick={() => checkPaymentStatus(lastCreated)} data-testid="check-payment-status-btn">
                        <RefreshCw className="w-4 h-4 mr-1" /> Verifica Pagamento
                      </Button>
                    </div>
                  )}

                  {/* Payment confirmed - emit receipt */}
                  {receiptStatus === 'paid' && (
                    <div className="space-y-2">
                      <p className="text-xs text-[#34C759] font-medium flex items-center gap-1">
                        <Check className="w-3 h-3" /> Pagamento confermato!
                      </p>
                      <Button size="sm" className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white w-full"
                        onClick={() => getReceipt(lastCreated)} data-testid="emit-receipt-btn">
                        <Receipt className="w-4 h-4 mr-1" /> Emetti Ricevuta Fiscale
                      </Button>
                    </div>
                  )}

                  {/* Receipt ready */}
                  {receiptStatus === 'receipt_ready' && (
                    <p className="text-xs text-[#34C759] font-medium flex items-center gap-1">
                      <Check className="w-3 h-3" /> Ricevuta fiscale emessa!
                    </p>
                  )}

                  {/* Loading states */}
                  {(receiptStatus === 'checking' || receiptStatus === 'creating') && (
                    <div className="flex items-center gap-2 text-sm text-[#71717A]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {receiptStatus === 'checking' ? 'Verifica in corso...' : 'Emissione ricevuta...'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Client selector */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Cliente</Label>
            {selectedClient ? (
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#09090B] truncate">{selectedClient.name}</p>
                  {selectedClient.phone && <p className="text-xs text-[#71717A]">{selectedClient.phone}</p>}
                </div>
                <Button size="sm" variant="ghost" onClick={clearClient} data-testid="clear-client-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  placeholder="Cerca cliente esistente o scrivi nuovo..."
                  value={clientSearch || form.client_name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setClientSearch(val);
                    setForm(f => ({ ...f, client_name: val }));
                    setShowClientDropdown(val.length > 0 && filteredClients.length > 0);
                  }}
                  onFocus={() => setShowClientDropdown(clientSearch.length > 0 && filteredClients.length > 0)}
                  data-testid="client-search-input"
                />
                {showClientDropdown && filteredClients.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-[#E4E4E7] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.map(c => (
                      <button key={c.client_id} onClick={() => selectClient(c)}
                        className="w-full text-left p-3 hover:bg-[#F4F4F5] transition-colors border-b border-[#F4F4F5] last:border-0"
                        data-testid={`select-client-${c.client_id}`}>
                        <p className="font-medium text-[#09090B]">{c.name}</p>
                        {c.address && <p className="text-xs text-[#71717A]">{c.address}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Phone + Address */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-semibold flex items-center gap-1">
                <Phone className="w-3 h-3" /> Telefono Cliente
              </Label>
              <Input value={form.client_phone} onChange={(e) => setForm(f => ({ ...f, client_phone: e.target.value }))}
                placeholder="es. 333 1234567" data-testid="client-phone-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Indirizzo
              </Label>
              <Input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="Via, Città" data-testid="client-address-input" />
            </div>
          </div>

          {/* Date */}
          <div>
            <Label className="text-sm font-semibold">Data Intervento</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
              data-testid="work-order-date" />
          </div>

          {/* Workers + Hours + Amount */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-sm font-semibold flex items-center gap-1">
                <Users className="w-3 h-3" /> N. Operai
              </Label>
              <Input type="number" min="1" value={form.workers_count}
                onChange={(e) => setForm(f => ({ ...f, workers_count: parseInt(e.target.value) || 1 }))}
                data-testid="workers-count-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold flex items-center gap-1">
                <Clock className="w-3 h-3" /> Ore Lavorate
              </Label>
              <Input type="number" min="0" step="0.5" value={form.hours_worked}
                onChange={(e) => setForm(f => ({ ...f, hours_worked: parseFloat(e.target.value) || 0 }))}
                data-testid="hours-worked-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold flex items-center gap-1">
                <Euro className="w-3 h-3" /> Importo €
              </Label>
              <Input type="number" min="0" step="0.01" value={form.amount}
                onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00" data-testid="amount-input" />
            </div>
          </div>

          {/* Receipt toggle + Payment mode */}
          {form.amount && parseFloat(form.amount) > 0 && (
            <div className="space-y-3 p-3 bg-[#F4F4F5] rounded-lg">
              {/* Receipt toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#09090B] flex items-center gap-1">
                    <Receipt className="w-4 h-4 text-[#8B5CF6]" /> Ricevuta Fiscale
                  </p>
                  <p className="text-xs text-[#71717A]">Ricevuta con IVA tramite SumUp</p>
                </div>
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, issue_receipt: !f.issue_receipt }))}
                  className={`w-11 h-6 rounded-full transition-colors relative ${form.issue_receipt ? 'bg-[#0055FF]' : 'bg-[#D4D4D8]'}`}
                  data-testid="issue-receipt-toggle">
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.issue_receipt ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Payment mode */}
              <div>
                <p className="text-sm font-semibold text-[#09090B] flex items-center gap-1 mb-2">
                  <CreditCard className="w-4 h-4 text-[#0070BA]" /> Modalità Pagamento
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, payment_mode: 'full', installments_count: 1 }))}
                    className={`p-2.5 rounded-lg border text-sm font-medium transition-all text-center
                      ${form.payment_mode !== 'installments' ? 'border-[#0055FF] bg-blue-50 text-[#0055FF]' : 'border-[#E4E4E7] text-[#71717A] hover:border-[#A1A1AA]'}`}
                    data-testid="payment-mode-full">
                    Pagamento Unico
                  </button>
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, payment_mode: 'installments', installments_count: f.installments_count > 1 ? f.installments_count : 3 }))}
                    className={`p-2.5 rounded-lg border text-sm font-medium transition-all text-center
                      ${form.payment_mode === 'installments' ? 'border-[#0055FF] bg-blue-50 text-[#0055FF]' : 'border-[#E4E4E7] text-[#71717A] hover:border-[#A1A1AA]'}`}
                    data-testid="payment-mode-installments">
                    Dilazionato
                  </button>
                </div>
              </div>

              {/* Installments count */}
              {form.payment_mode === 'installments' && (
                <div>
                  <Label className="text-xs font-medium text-[#71717A]">Numero Rate</Label>
                  <div className="flex gap-2 mt-1">
                    {[2, 3, 4, 5, 6].map(n => (
                      <button key={n} type="button"
                        onClick={() => setForm(f => ({ ...f, installments_count: n }))}
                        className={`w-10 h-10 rounded-lg border text-sm font-bold transition-all
                          ${form.installments_count === n ? 'border-[#0055FF] bg-[#0055FF] text-white' : 'border-[#E4E4E7] text-[#71717A] hover:border-[#A1A1AA]'}`}
                        data-testid={`installments-${n}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#A1A1AA] mt-1.5">
                    {form.installments_count} rate da € {(parseFloat(form.amount) * 1.22 / form.installments_count).toFixed(2)} ciascuna (IVA inclusa)
                  </p>
                </div>
              )}

              {/* PayPal Pay in 3 note */}
              <p className="text-xs text-[#0070BA] italic">
                Con PayPal il cliente può anche scegliere "Paga in 3 rate" senza interessi (€30-€2.000)
              </p>
            </div>
          )}

          {/* Description with voice */}
          <div>
            <Label className="text-sm font-semibold flex items-center justify-between">
              Descrizione Intervento
              {voiceSupported && (
                <Button type="button" size="sm" variant={listening === 'description' ? 'default' : 'outline'}
                  onClick={() => toggleVoice('description')} data-testid="voice-description-btn"
                  className={listening === 'description' ? 'bg-[#FF3B30] hover:bg-[#E63329] text-white animate-pulse' : ''}>
                  {listening === 'description' ? <MicOff className="w-3 h-3 mr-1" /> : <Mic className="w-3 h-3 mr-1" />}
                  {listening === 'description' ? 'Stop' : 'Voce'}
                </Button>
              )}
            </Label>
            <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Descrivi l'intervento..." rows={3} data-testid="description-input" />
          </div>

          {/* Materials with voice */}
          <div>
            <Label className="text-sm font-semibold flex items-center justify-between">
              Materiali Utilizzati
              {voiceSupported && (
                <Button type="button" size="sm" variant={listening === 'materials' ? 'default' : 'outline'}
                  onClick={() => toggleVoice('materials')} data-testid="voice-materials-btn"
                  className={listening === 'materials' ? 'bg-[#FF3B30] hover:bg-[#E63329] text-white animate-pulse' : ''}>
                  {listening === 'materials' ? <MicOff className="w-3 h-3 mr-1" /> : <Mic className="w-3 h-3 mr-1" />}
                  {listening === 'materials' ? 'Stop' : 'Voce'}
                </Button>
              )}
            </Label>
            <Textarea value={form.materials} onChange={(e) => setForm(f => ({ ...f, materials: e.target.value }))}
              placeholder="Elenca i materiali..." rows={2} data-testid="materials-input" />
          </div>

          {/* Digital Signature */}
          <div>
            <Label className="text-sm font-semibold">Firma Cliente</Label>
            <div className="border-2 border-dashed border-[#E4E4E7] rounded-lg overflow-hidden bg-white relative">
              <canvas ref={canvasRef} className="w-full touch-none"
                style={{ height: '150px', cursor: 'crosshair' }}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                data-testid="signature-canvas" />
              {!signature && (
                <p className="absolute inset-0 flex items-center justify-center text-[#A1A1AA] text-sm pointer-events-none">
                  Firma qui
                </p>
              )}
            </div>
            {signature && (
              <Button size="sm" variant="outline" className="mt-2" onClick={clearSignature} data-testid="clear-signature-btn">
                <X className="w-3 h-3 mr-1" /> Cancella Firma
              </Button>
            )}
          </div>

          {/* Submit */}
          <Button onClick={handleSubmit} disabled={saving} className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold py-5"
            data-testid="submit-work-order-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
            {saving ? 'Salvataggio...' : 'Crea Buono di Lavoro'}
          </Button>
        </CardContent>
      )}
    </Card>
  );
};

export default WorkOrderForm;
